import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowLeft, ChevronLeft, ChevronRight, Inbox, Search, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { AccountDto } from '@/Domain/api/User/dto/Account.dto';
import { SonyDocumentStatusBadge } from '@/Domain/components/KnowledgeBase/SonyDocumentStatusBadge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Domain/components/Shared/Table/Table';
import {
  SonyAdminDocumentDto,
  SonyAdminDocumentsListDto,
  SonyDocumentStatusEnum,
  SonyDocumentsFilter,
  SonyObjectTypeEnum,
} from '@/UI/KnowledgeBase/SonyDocuments/SonyDocuments.dto';
import { hasSonyAdminAccess } from '@/UI/KnowledgeBase/SonyDocuments/sonyAdminAccess';

const SONY_DOCUMENTS_PAGE_LIMIT = 50;
const FILTER_DEBOUNCE_MS = 500;
const ALL_FILTER_VALUE = 'all';

export const SonyDocumentsPage = () => {
  const { t } = useTranslation('sony_admin');
  const navigate = useNavigate();
  // Reactive read: BootstrapProvider populates ['account'] in an effect after the first
  // commit, so a non-reactive getQueryData reads undefined and wrongly redirects on hard
  // refresh or deep link. enabled:false subscribes to the cache without issuing a fetch.
  const { data: account } = useQuery<AccountDto>({ queryKey: ['account'], enabled: false });
  const canAccess = hasSonyAdminAccess(account);

  const [statusFilter, setStatusFilter] = useState<string>(ALL_FILTER_VALUE);
  const [objectTypeFilter, setObjectTypeFilter] = useState<string>(ALL_FILTER_VALUE);
  const [titleSearch, setTitleSearch] = useState('');
  const [externalIdSearch, setExternalIdSearch] = useState('');
  const [debouncedTitle] = useDebounceValue(titleSearch, FILTER_DEBOUNCE_MS);
  const [debouncedExternalId] = useDebounceValue(externalIdSearch, FILTER_DEBOUNCE_MS);
  const [pageIndex, setPageIndex] = useState(0);

  const statusSelected = statusFilter !== ALL_FILTER_VALUE;

  const filters = useMemo<SonyDocumentsFilter>(() => {
    const result: SonyDocumentsFilter = {};
    if (statusFilter !== ALL_FILTER_VALUE) result.status = statusFilter as SonyDocumentStatusEnum;
    if (objectTypeFilter !== ALL_FILTER_VALUE) {
      result.object_type = objectTypeFilter as SonyObjectTypeEnum;
    }
    // The backend rejects a title filter without a status filter (400).
    if (result.status && debouncedTitle.trim()) result.title = debouncedTitle.trim();
    if (debouncedExternalId.trim()) result.external_id = debouncedExternalId.trim();
    return result;
  }, [statusFilter, objectTypeFilter, debouncedTitle, debouncedExternalId]);

  const offset = pageIndex * SONY_DOCUMENTS_PAGE_LIMIT;
  const { data, isLoading } = useQuery<SonyAdminDocumentsListDto>({
    queryKey: [QueryKey.sonyAdmin.documents, { offset, limit: SONY_DOCUMENTS_PAGE_LIMIT, filters }],
    enabled: canAccess,
    placeholderData: keepPreviousData,
  });

  if (account === undefined) {
    return (
      <div
        className="flex flex-1 items-center justify-center py-12"
        data-testid="sony-access-loading"
      >
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!canAccess) {
    return <Navigate to={`../${IndexPath.knowledgeBase.root}`} replace />;
  }

  const documents = data?.items ?? [];
  const totalItems = data?.count ?? null;
  const countCapped = data?.count_capped ?? false;
  const pageCount =
    totalItems !== null && !countCapped
      ? Math.max(1, Math.ceil(totalItems / SONY_DOCUMENTS_PAGE_LIMIT))
      : null;
  const hasNextPage =
    pageCount !== null ? pageIndex < pageCount - 1 : documents.length === SONY_DOCUMENTS_PAGE_LIMIT;
  const isFiltered = Object.keys(filters).length > 0;

  const resetFilters = () => {
    setStatusFilter(ALL_FILTER_VALUE);
    setObjectTypeFilter(ALL_FILTER_VALUE);
    setTitleSearch('');
    setExternalIdSearch('');
    setPageIndex(0);
  };

  const handleRowClick = (sonyDocument: SonyAdminDocumentDto): void => {
    navigate(`documents/${sonyDocument.external_id}`, { viewTransition: true });
  };

  return (
    <div className="flex flex-1 flex-col min-w-0 max-w-full">
      <div className="mb-6 flex flex-col items-start gap-2 md:flex-row md:items-baseline md:gap-6">
        <h1 className="text-lg font-semibold text-foreground">{t('title')}</h1>
        <span className="text-sm text-muted-foreground">{t('subtitle')}</span>
        <div className="md:ml-auto">
          <Button variant="secondary" size="sm" asChild data-testid="sony-back-button">
            <Link to={`../${IndexPath.knowledgeBase.root}`}>
              <ArrowLeft className="size-4" />
              {t('back')}
            </Link>
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder={t('filters.search_placeholder')}
          value={titleSearch}
          onChange={(event) => {
            setTitleSearch(event.target.value);
            setPageIndex(0);
          }}
          size="sm"
          className="w-[220px]"
          startIcon={<Search className="h-4 w-4" />}
          disabled={!statusSelected}
          data-testid="sony-title-search"
        />
        {!statusSelected && (
          <span className="text-xs text-muted-foreground" data-testid="sony-title-search-hint">
            {t('filters.title_requires_status')}
          </span>
        )}
        <Input
          placeholder={t('filters.external_id_placeholder')}
          value={externalIdSearch}
          onChange={(event) => {
            setExternalIdSearch(event.target.value);
            setPageIndex(0);
          }}
          size="sm"
          className="w-[200px]"
          data-testid="sony-external-id-search"
        />
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value);
            if (value === ALL_FILTER_VALUE) setTitleSearch('');
            setPageIndex(0);
          }}
        >
          <SelectTrigger
            size="xs"
            className="w-[170px]"
            aria-label={t('filters.status')}
            data-testid="sony-status-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>{t('filters.all_statuses')}</SelectItem>
            {Object.values(SonyDocumentStatusEnum).map((status) => (
              <SelectItem key={status} value={status}>
                {t(`status.${status}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={objectTypeFilter}
          onValueChange={(value) => {
            setObjectTypeFilter(value);
            setPageIndex(0);
          }}
        >
          <SelectTrigger
            size="xs"
            className="w-[190px]"
            aria-label={t('filters.object_type')}
            data-testid="sony-object-type-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>{t('filters.all_object_types')}</SelectItem>
            {Object.values(SonyObjectTypeEnum).map((objectType) => (
              <SelectItem key={objectType} value={objectType}>
                {objectType}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isFiltered && (
          <Button variant="ghost" size="sm" onClick={resetFilters} className="px-2">
            {t('filters.reset')}
            <XCircle className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-12">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        <div className="flex flex-1 flex-col space-y-4">
          <div className="custom-scroll relative flex w-full flex-1 flex-col overflow-x-auto">
            <Table className="min-w-full" data-testid="sony-documents-table">
              <TableHeader className="sticky top-0 z-20 bg-accent">
                <TableRow>
                  {(
                    [
                      'external_id',
                      'title',
                      'object_type',
                      'locale',
                      'status',
                      'version_timestamp',
                      'last_updated',
                    ] as const
                  ).map((column) => (
                    <TableHead
                      key={column}
                      className="whitespace-nowrap bg-accent text-sm font-medium text-muted-foreground"
                    >
                      {t(`table.${column}`)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((sonyDocument) => (
                  <TableRow
                    key={sonyDocument.id}
                    className="cursor-pointer hover:!bg-accent"
                    onClick={() => handleRowClick(sonyDocument)}
                    data-testid={`sony-document-row-${sonyDocument.external_id}`}
                  >
                    <TableCell className="whitespace-nowrap font-mono text-sm text-foreground">
                      {sonyDocument.external_id}
                    </TableCell>
                    <TableCell className="max-w-[320px]">
                      <span
                        className="block truncate text-sm text-foreground"
                        title={sonyDocument.title}
                      >
                        {sonyDocument.title}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {sonyDocument.object_type}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {sonyDocument.locale}
                    </TableCell>
                    <TableCell>
                      <SonyDocumentStatusBadge status={sonyDocument.status} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                      {sonyDocument.version_timestamp ?? ''}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {format(new Date(sonyDocument.last_updated_timestamp), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {documents.length === 0 && (
              <div className="my-12 flex flex-col items-center justify-center gap-3">
                <Inbox className="h-8 w-8 text-foreground-subtle" />
                <span className="text-sm font-semibold text-foreground">
                  {t('table.empty_title')}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t('table.empty_description')}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between px-2">
            <div className="flex-1 text-sm text-muted-foreground" data-testid="sony-total-count">
              {totalItems !== null &&
                (countCapped
                  ? t('pagination.total_capped', { count: totalItems })
                  : t('pagination.total', { count: totalItems }))}
            </div>
            <div className="flex items-center gap-4">
              {pageCount !== null && (
                <span className="text-sm font-medium">
                  {t('pagination.page_of', { current: pageIndex + 1, total: pageCount })}
                </span>
              )}
              <div className="flex items-center gap-2">
                <Button
                  variant="white"
                  className="h-8 w-8 p-0"
                  onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
                  disabled={pageIndex === 0}
                  data-testid="sony-pagination-previous"
                >
                  <span className="sr-only">{t('pagination.previous')}</span>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="white"
                  className="h-8 w-8 p-0"
                  onClick={() => setPageIndex((prev) => prev + 1)}
                  disabled={!hasNextPage}
                  data-testid="sony-pagination-next"
                >
                  <span className="sr-only">{t('pagination.next')}</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Outlet renders SonyDocumentSheet when visiting /documents/:externalId routes */}
      <Outlet />
    </div>
  );
};
